import React, { useState, SetStateAction, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  InputLabel,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { ptBR } from 'date-fns/locale';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { useStyles } from './styles';
import { Projeto } from '../../@types/engineeringTypes';
import { DeferidoList } from '../../pages/AddEngineering/utils';
import SubInputWarn from '../SubInputWarn';

interface IProps {
  project: Projeto;
  setProject: React.Dispatch<SetStateAction<Projeto>>;
  minDate: string;
  formErrors: { key: string; message: string }[];
  setFormErrors: React.Dispatch<SetStateAction<{ key: string; message: string }[]>>;
}

const ProjectForm: React.FC<IProps> = ({
  project,
  setProject,
  minDate,
  formErrors,
  setFormErrors,
}) => {
  const [projectState, setProjectState] = useState<Projeto>(project);
  const classes = useStyles();
  const [errors, setErrors] = useState<{ key: string; message: string }[]>([]);

  const handleChange = (key: string, value: string) => {
    setProjectState((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const handlePeriodChange = (date: Date | null) => {
    if (date?.getDate()) {
      setProject((prev) => {
        return { ...prev, 'Data de Elaboração do Projeto': date.toISOString() };
      });
    }
  };

  useEffect(() => {
    setProjectState(project);
    setErrors(formErrors);
  }, [project, formErrors]);

  return (
    <Box className={classes.sectionContainer}>
      <Typography variant="h6">Projeto da Intervenção</Typography>
      <Grid container spacing={2}>
        
        <Grid item xs={12} md={12}>
          <InputLabel className={classes.label}>Identificador do Projeto</InputLabel>
          <TextField
            name="Identificador do Projeto"
            placeholder="Identificador do Projeto"
            onChange={(event) => {
              handleChange('Identificador do Projeto', event.target.value);
            }}
            value={projectState['Identificador do Projeto']}
            onBlur={() => setProject(projectState)}
            variant="outlined"
            size="small"
            className={classes.textField}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputLabel className={classes.label}>Responsável pelo Projeto</InputLabel>
          <TextField
            name="Responsável"
            placeholder="Responsável"
            onChange={(event) => {
              handleChange('Responsável', event.target.value);
            }}
            value={projectState.Responsável}
            onBlur={() => setProject(projectState)}
            variant="outlined"
            size="small"
            className={classes.textField}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputLabel className={classes.label}>Data de Elaboração</InputLabel>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
            <KeyboardDatePicker
              clearable
              variant="inline"
              format="dd/MM/yyyy"
              placeholder="dd/mm/aaaa"
              className={classes.dateInput}
              value={projectState['Data de Elaboração do Projeto']}
              minDate={new Date(minDate)}
              onChange={(event) => {
                handlePeriodChange(event);
                setFormErrors((prev) => {
                  return prev.filter(
                    (item) =>
                      item.key !== 'dataProjeto' &&
                      item.key !== 'dataProjeto-el' &&
                      item.key !== 'dataProjeto-init',
                  );
                });
              }}
              inputVariant="outlined"
              inputProps={{ style: { padding: '0 0 0 15px', height: 40 } }}
              showTodayButton
              autoOk
              error={
                !!formErrors.filter(
                  (item) =>
                    item.key === 'dataProjeto' ||
                    item.key === 'dataProjeto-el' ||
                    item.key === 'dataProjeto-init',
                ).length
              }
              helperText=""
            />
          </MuiPickersUtilsProvider>
          <SubInputWarn
            type="error"
            formWarns={errors.filter(
              (item) =>
                item.key === 'dataProjeto' ||
                item.key === 'dataProjeto-el' ||
                item.key === 'dataProjeto-init',
            )}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <InputLabel className={classes.label}>
            Identificador do Projeto Dependente
          </InputLabel>
          <TextField
            name="Identificador do Projeto Dependente"
            placeholder="Identificador do Projeto Dependente"
            onChange={(event) => {
              handleChange('Identificador do Projeto Dependente', event.target.value);
            }}
            value={projectState['Identificador do Projeto Dependente']}
            onBlur={() => setProject(projectState)}
            variant="outlined"
            size="small"
            className={classes.textField}
            disabled={projectState['Projeto Dependente'] === 'Não'}
          />
        </Grid>
        <Grid item xs={12} md={4} style={{display: 'flex', alignItems: 'flex-end'}}>
          <FormControlLabel
            label="Projeto Dependente"
            control={(
              <Checkbox
                checked={projectState['Projeto Dependente'] === 'Sim'}
                color="primary"
                onClick={() => {
                  setProjectState((prev) => {
                    const val = prev['Projeto Dependente'] === 'Não' ? 'Sim' : 'Não';
                    return {
                      ...prev,
                      'Projeto Dependente': val,
                      'Identificador do Projeto Dependente': '',
                    };
                  });
                }}
                onBlur={() => setProject(projectState)}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label="Projeto com mais de uma Intervenção"
            control={(
              <Checkbox
                checked={projectState['Projeto com mais de uma Intervenção'] === 'Sim'}
                color="primary"
                onClick={() => {
                  setProjectState((prev) => {
                    const val =
                      prev['Projeto com mais de uma Intervenção'] === 'Não'
                        ? 'Sim'
                        : 'Não';
                    return { ...prev, 'Projeto com mais de uma Intervenção': val };
                  });
                }}
                onBlur={() => setProject(projectState)}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectForm;
